import { SpeakButton } from "components/atoms";
import { Bubbles } from "components/molecules";
import {
  BackButton,
  Card,
  CardTitle,
  Content,
  Page,
  TopBar,
} from "components/templates";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useAppLanguage } from "context/AppLanguageContext";
import { learnHref, topicHref } from "utils/learnPaths";
import { findLearnTopic, learnTopics } from "utils/learnTopics";
import { AvailableLanguages } from "utils/types";

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const TopicEmoji = styled.span`
  font-size: 2rem;
  line-height: 1;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: "Gaegu", cursive;
  font-size: 2.1rem;
  color: #f5e2c8;
  text-shadow: 2px 5px 3px rgba(0, 0, 0, 0.35);
`;

const Summary = styled.p`
  margin: 0 0 22px;
  max-width: 90%;
  font-family: "Gaegu", cursive;
  font-size: 1.2rem;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.85);
`;

const SectionCard = styled(Card)`
  margin-bottom: 14px;
`;

const SectionHeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionHeading = styled.h2`
  margin: 0 0 8px;
  font-family: "Finger Paint", cursive;
  font-size: 1.15rem;
  letter-spacing: 0.2px;
  color: rgba(173, 216, 230, 0.95);
`;

const Paragraph = styled.p`
  margin: 0 0 10px;
  font-family: "Gaegu", cursive;
  font-size: 1.18rem;
  line-height: 1.45;
  color: rgba(245, 226, 200, 0.95);

  &:last-child {
    margin-bottom: 0;
  }
`;

const BulletList = styled.ul`
  margin: 0;
  padding: 0 0 0 18px;
  display: grid;
  gap: 6px;
`;

const BulletItem = styled.li`
  font-family: "Gaegu", cursive;
  font-size: 1.15rem;
  line-height: 1.35;
  color: rgba(245, 226, 200, 0.95);
`;

const RelatedSection = styled.div`
  margin-top: 28px;
`;

const RelatedLabel = styled.h2`
  margin: 0 0 10px;
  font-family: "Finger Paint", cursive;
  font-size: 0.95rem;
  letter-spacing: 0.2px;
  color: rgba(245, 226, 200, 0.7);
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const RelatedCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.18);
  background: rgba(27, 38, 79, 0.24);
  color: #f5e2c8;
  font-family: "Gaegu", cursive;
  font-size: 1.05rem;
  line-height: 1.25;
  text-decoration: none;
  transition:
    transform 120ms ease,
    border-color 120ms ease,
    background 120ms ease;

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(173, 216, 230, 0.55);
    background: rgba(27, 38, 79, 0.4);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const RelatedEmoji = styled.span`
  font-size: 1.2rem;
  line-height: 1;
  flex: 0 0 auto;
`;

const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(245, 226, 200, 0.28);
  background: rgba(27, 38, 79, 0.28);
  color: #f5e2c8;
  font-family: "Finger Paint", cursive;
  font-size: 0.9rem;
  text-decoration: none;
  transition:
    transform 120ms ease,
    background 120ms ease;

  &:hover {
    transform: translateY(-1px);
    background: rgba(27, 38, 79, 0.44);
  }

  &:focus-visible {
    outline: 2px solid rgba(173, 216, 230, 0.85);
    outline-offset: 2px;
  }
`;

const ui = {
  en: {
    back: "Back",
    backAria: "Go back",
    allTopics: "All topics",
    notFoundTitle: "Topic not found",
    notFoundBody: "We couldn't find that topic. Try browsing the full list.",
    listenSection: (heading: string) => `Listen: ${heading}`,
    previous: "← Previous",
    next: "Next →",
    relatedTopics: "Related topics",
  },
  es: {
    back: "Volver",
    backAria: "Volver",
    allTopics: "Todos los temas",
    notFoundTitle: "Tema no encontrado",
    notFoundBody: "No encontramos ese tema. Probá ver la lista completa.",
    listenSection: (heading: string) => `Escuchar: ${heading}`,
    previous: "← Anterior",
    next: "Siguiente →",
    relatedTopics: "Temas relacionados",
  },
};

export const LearnTopic = () => {
  const { topicSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useAppLanguage();
  const isSpanish = language === AvailableLanguages.ES;
  const t = isSpanish ? ui.es : ui.en;

  const topic = findLearnTopic(topicSlug);
  const topicIndex = topic ? learnTopics.indexOf(topic) : -1;
  const previousTopic =
    topicIndex > 0 ? learnTopics[topicIndex - 1] : undefined;
  const nextTopic =
    topicIndex >= 0 && topicIndex < learnTopics.length - 1
      ? learnTopics[topicIndex + 1]
      : undefined;

  if (!topic) {
    return (
      <Page id="learn-topic" lang={isSpanish ? "es" : "en"}>
        <Bubbles />
        <Content>
          <TopBar>
            <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
              <span aria-hidden="true">←</span> {t.back}
            </BackButton>
          </TopBar>
          <Card>
            <CardTitle>{t.notFoundTitle}</CardTitle>
            <Paragraph>{t.notFoundBody}</Paragraph>
            <NavLink to={learnHref(isSpanish)}>{t.allTopics}</NavLink>
          </Card>
        </Content>
      </Page>
    );
  }

  const locale = isSpanish ? topic.es : topic.en;
  const relatedTopics = (topic.related ?? [])
    .map((slug) => findLearnTopic(slug))
    .filter((t): t is NonNullable<typeof t> => !!t);

  return (
    <Page id="learn-topic" lang={isSpanish ? "es" : "en"}>
      <Bubbles />
      <Content>
        <TopBar>
          <BackButton onClick={() => navigate(-1)} aria-label={t.backAria}>
            <span aria-hidden="true">←</span> {t.back}
          </BackButton>
        </TopBar>

        <TitleRow>
          <TopicEmoji aria-hidden="true">{topic.emoji}</TopicEmoji>
          <PageTitle>{locale.title}</PageTitle>
          <SpeakButton
            text={locale.title}
            language={language}
            label={t.listenSection(locale.title)}
          />
        </TitleRow>
        <Summary>{locale.summary}</Summary>

        {locale.sections.map((section) => {
          const speakableText = [
            section.heading,
            ...(section.paragraphs ?? []),
            ...(section.list ?? []),
          ].join(". ");

          return (
            <SectionCard key={section.heading}>
              <SectionHeadingRow>
                <SectionHeading>{section.heading}</SectionHeading>
                <SpeakButton
                  text={speakableText}
                  language={language}
                  label={t.listenSection(section.heading)}
                />
              </SectionHeadingRow>
              {section.paragraphs?.map((paragraph) => (
                <Paragraph key={paragraph}>{paragraph}</Paragraph>
              ))}
              {section.list && (
                <BulletList>
                  {section.list.map((item) => (
                    <BulletItem key={item}>{item}</BulletItem>
                  ))}
                </BulletList>
              )}
            </SectionCard>
          );
        })}

        {relatedTopics.length > 0 && (
          <RelatedSection>
            <RelatedLabel>{t.relatedTopics}</RelatedLabel>
            <RelatedGrid>
              {relatedTopics.map((related) => {
                const relatedLocale = isSpanish ? related.es : related.en;
                return (
                  <RelatedCard key={related.slug} to={topicHref(isSpanish, related)}>
                    <RelatedEmoji aria-hidden="true">
                      {related.emoji}
                    </RelatedEmoji>
                    {relatedLocale.title}
                  </RelatedCard>
                );
              })}
            </RelatedGrid>
          </RelatedSection>
        )}

        <NavRow>
          {previousTopic ? (
            <NavLink to={topicHref(isSpanish, previousTopic)}>
              {t.previous}{" "}
              {isSpanish ? previousTopic.es.title : previousTopic.en.title}
            </NavLink>
          ) : (
            <span />
          )}
          {nextTopic ? (
            <NavLink to={topicHref(isSpanish, nextTopic)}>
              {isSpanish ? nextTopic.es.title : nextTopic.en.title} {t.next}
            </NavLink>
          ) : (
            <span />
          )}
        </NavRow>
      </Content>
    </Page>
  );
};
